import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const EvaluationSetup = () => {
  const [openings, setOpenings] = useState([]);
  const [selectedOpening, setSelectedOpening] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [statusMessage, setStatusMessage] = useState(null);

  useEffect(() => {
    // Fetch published job openings
    const fetchOpenings = async () => {
      try {
        const response = await api.get('/api/requisitions/global');
        const all = Array.isArray(response.data) ? response.data : [];
        setOpenings(all);
        if (all.length > 0) {
          setSelectedOpening(all[0].id.toString());
        }
      } catch (err) {
        console.warn('⚠️ Fetch openings failed, using mock data.');
        setOpenings([
          { id: 1, title: 'Senior React Developer' },
          { id: 2, title: 'Node.js Backend Engineer' }
        ]);
        setSelectedOpening('1');
      }
    };

    const fetchTemplates = async () => {
      try {
        const response = await api.get('/api/evaluations/templates');
        setTemplates(response.data);
      } catch (err) {
        console.warn('⚠️ Fetch templates list failed. Setting mock template list.');
        setTemplates([
          { id: 1, file_name: 'frontend_evaluation_rubric_v2.docx', job_title: 'Senior React Developer', uploaded_at: '2026-08-02T11:00:00.000Z' },
          { id: 2, file_name: 'backend_eval_standard.pdf', job_title: 'Node.js Backend Engineer', uploaded_at: '2026-08-03T15:20:00.000Z' }
        ]);
      }
    };

    fetchOpenings();
    fetchTemplates();
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOpening || !file) {
      setStatusMessage({ type: 'error', text: 'Please select a job opening and choose a file to upload.' });
      return;
    }

    setLoading(true);
    setStatusMessage(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('jobOpeningId', selectedOpening);

    try {
      await api.post('/api/evaluations/templates/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setStatusMessage({ type: 'success', text: 'Interviewer evaluation form template uploaded successfully!' });
      setFile(null);
    } catch (err) {
      console.warn('⚠️ File upload API endpoint failed. Simulating local file upload state...');
      setTimeout(() => {
        const matchingJob = openings.find(o => o.id.toString() === selectedOpening);
        const newTemplate = {
          id: Date.now(),
          file_name: file.name,
          job_title: matchingJob ? matchingJob.title : 'General Role',
          uploaded_at: new Date().toISOString()
        };
        setTemplates(prev => [newTemplate, ...prev]);
        setFile(null);
        setStatusMessage({ type: 'success', text: `Mock upload succeeded! Template associated with ${newTemplate.job_title}.` });
      }, 1500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h2 style={styles.title}>Interviewer Evaluation Setup</h2>
        <p style={styles.subtitle}>Upload evaluation rubrics, checklists, or standard feedback templates for specific roles</p>
      </header>

      {statusMessage && (
        <div style={{
          ...styles.statusBox,
          background: statusMessage.type === 'error' ? 'var(--alert-critical-bg)' : statusMessage.type === 'success' ? 'rgba(16, 185, 129, 0.12)' : 'rgba(59, 130, 246, 0.12)',
          borderColor: statusMessage.type === 'error' ? 'var(--alert-critical-border)' : statusMessage.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)',
          color: statusMessage.type === 'error' ? 'var(--danger-color)' : statusMessage.type === 'success' ? 'var(--success-color)' : '#3b82f6'
        }}>
          {statusMessage.text}
        </div>
      )}

      <div style={styles.grid}>
        {/* UPLOADER PANEL */}
        <div style={styles.uploaderCard}>
          <h3 style={styles.cardTitle}>Upload New Template</h3>
          <form onSubmit={handleUploadSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Map to Job Position</label>
              <select
                value={selectedOpening}
                onChange={(e) => setSelectedOpening(e.target.value)}
                style={styles.select}
              >
                {openings.map(o => (
                  <option key={o.id} value={o.id}>{o.title}</option>
                ))}
              </select>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Choose Template File (.docx, .pdf, .txt)</label>
              <div style={styles.fileDropZone}>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileChange}
                  style={styles.fileInput}
                  id="template-file"
                />
                <label htmlFor="template-file" style={styles.fileLabel}>
                  <svg style={styles.uploadIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span style={styles.fileText}>
                    {file ? file.name : 'Click to select or drag file here'}
                  </span>
                  {file && <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>}
                </label>
              </div>
            </div>

            <button type="submit" disabled={loading} style={styles.submitBtn}>
              {loading ? 'Uploading template...' : 'Save Template'}
            </button>
          </form>
        </div>

        {/* LIST DISPLAY */}
        <div style={styles.listCard}>
          <h3 style={styles.cardTitle}>Active Evaluation Rubrics</h3>
          <div style={styles.list}>
            {templates.map((tpl) => (
              <div key={tpl.id} style={styles.templateItem}>
                <div style={styles.tplIconWrapper}>
                  <svg style={styles.tplIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div style={styles.tplInfo}>
                  <p style={styles.tplName}>{tpl.file_name}</p>
                  <p style={styles.tplMeta}>
                    Mapped to: <strong>{tpl.job_title}</strong> • Uploaded: {new Date(tpl.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '800',
    letterSpacing: '-0.5px'
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: 'var(--text-muted)'
  },
  statusBox: {
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    border: '1px solid transparent'
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  },
  uploaderCard: {
    flex: '1 1 350px',
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
    boxSizing: 'border-box'
  },
  listCard: {
    flex: '1.5 1 450px',
    background: 'var(--bg-card-glass)',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-light)',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: 'var(--shadow-sm)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  cardTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '750',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--divider-color)',
    paddingBottom: '12px',
    marginBottom: '8px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--border-input)',
    fontSize: '13px',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
    outline: 'none'
  },
  fileDropZone: {
    border: '2px dashed var(--border-input)',
    borderRadius: '12px',
    background: 'var(--bg-input)',
    padding: '28px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    position: 'relative',
    transition: 'border-color 0.2s',
    ':hover': {
      borderColor: 'var(--border-input-focus)'
    }
  },
  fileInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer'
  },
  fileLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer'
  },
  uploadIcon: {
    width: '32px',
    height: '32px',
    color: 'var(--text-muted)'
  },
  fileText: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)'
  },
  fileSize: {
    fontSize: '11px',
    color: 'var(--text-muted)'
  },
  submitBtn: {
    background: 'linear-gradient(135deg, var(--primary-color) 0%, #3b82f6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 24px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
    marginTop: '8px'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxHeight: '380px',
    overflowY: 'auto'
  },
  templateItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-color)',
    borderRadius: '12px',
    padding: '12px 16px'
  },
  tplIconWrapper: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(79, 70, 229, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--primary-color)'
  },
  tplIcon: {
    width: '20px',
    height: '20px'
  },
  tplInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  tplName: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '700',
    color: 'var(--text-primary)'
  },
  tplMeta: {
    margin: 0,
    fontSize: '11px',
    color: 'var(--text-muted)'
  }
};

export default EvaluationSetup;
